import { REGULAR_SECTORS } from './runConstants';

// Each type has ten encounters. Enemy HP and damage scale with the sector;
// shard payouts stay fixed. Chosen IDs are saved so renders never reroll a door.
const type = (
  id,
  kind,
  color,
  target,
  damage,
  shards,
  heal,
  bonusRelic,
  encounters
) => ({
  id,
  kind,
  color,
  target,
  damage,
  shards,
  heal,
  bonusRelic,
  encounters: encounters.map(([name, description], index) => ({
    id: `${id}-${index + 1}`,
    name,
    description,
  })),
});

export const DOOR_TYPES = [
  type('recovery', 'Recovery', '#89ffac', 36, 2, 1, 3, false, [
    [
      'Flickering Infirmary',
      'Restart a sparking med station before it shorts out.',
    ],
    ['Cooling Chamber', 'Stabilize a cooling field to soothe your burns.'],
    ['Broken Clinic', 'Recover sealed medicine from the abandoned clinic.'],
    ['Last Safe Signal', 'Trace a faint distress beacon to a medical shelter.'],
    ['Pulse Ward', 'Match the heartbeat of a malfunctioning healing pod.'],
    ['Nanite Bath', 'Filter the rogue nanites from a restorative pool.'],
    ['Emergency Bunker', 'Force open the bunker to reach its first aid kit.'],
    ['Stasis Bed', 'Wake the dormant bed without triggering its alarms.'],
    ['Medic Drone Nest', 'Convince the damaged drones to treat your wounds.'],
    ['Life Support Hub', 'Reconnect the oxygen lines and catch your breath.'],
  ]),
  type('treasure', 'Treasure', '#ffd577', 55, 3, 3, 0, false, [
    ['Data Vault', 'Crack the vault before its security wakes.'],
    ['Laser Gallery', 'Slip through the beams to reach a chest of shards.'],
    ['The Mimic Cache', 'Open a chest that might open you first.'],
    ['The Crown Circuit', 'Steal a gilded chest from the Core itself.'],
    ['Sunken Strongbox', 'Drain a flooded conduit to uncover its strongbox.'],
    ['Prism Treasury', 'Align the crystal locks around a radiant chest.'],
    ['Smuggler Locker', 'Decode a stolen key to claim the hidden stash.'],
    ['Royal Backup', 'Bypass a forgotten monarch’s encrypted reserve.'],
    ['Magnetic Safe', 'Reverse the magnetic seal before the safe snaps shut.'],
    [
      'Ghost Payday',
      'Intercept a phantom delivery carrying a chest of shards.',
    ],
  ]),
  type('bonus-relic', 'Bonus Relic', '#d18aff', 62, 3, 0, 0, true, [
    ['Relic Reliquary', 'Release an unclaimed relic from its sealed pedestal.'],
    [
      'Forgotten Exhibit',
      'Disable the display’s defenses and take its artifact.',
    ],
    ['Prototype Locker', 'Override the lock around an experimental relic.'],
    ['Echo Archive', 'Find the real relic among its flickering duplicates.'],
    ['Lost Courier', 'Recover a relic from a courier trapped in a loop.'],
    ['Crystal Cradle', 'Extract an artifact without shattering its cradle.'],
    ['Signal Museum', 'Restore a lost frequency to unlock an ancient relic.'],
    [
      'Sealed Workshop',
      'Finish the inventor’s circuit to release their creation.',
    ],
    ['Satellite Shrine', 'Catch a relic falling through a broken uplink.'],
    ['Memory Capsule', 'Solve a preserved memory to claim its artifact.'],
  ]),
  type('salvage', 'Salvage', '#a6c4da', 30, 1, 2, 0, false, [
    ['Scrap Conveyor', 'Pick useful shards from a moving belt of debris.'],
    ['Drone Graveyard', 'Strip the dead drones before their batteries flare.'],
    ['Discard Chute', 'Catch valuable scrap between falling loads.'],
    ['Cable Warren', 'Untangle live wires to reach abandoned components.'],
    ['Rust Depot', 'Pry salvage out of a collapsing storage rack.'],
    ['Broken Elevator', 'Recover spare parts from its stalled machinery.'],
    ['Cooling Scrapheap', 'Sift cooling metal for intact shard cells.'],
    [
      'Maintenance Crawlspace',
      'Crawl past exposed gears to reach a parts bin.',
    ],
    [
      'Abandoned Terminal',
      'Dismantle the terminal before its capacitor bursts.',
    ],
    ['Wrecked Shuttle', 'Scavenge a crashed delivery shuttle’s cargo bay.'],
  ]),
  type('sanctuary', 'Sanctuary', '#91e6df', 46, 2, 0, 5, false, [
    ['Quiet Garden', 'Restore the garden’s shield to rest beneath its leaves.'],
    [
      'Moonlit Refuge',
      'Seal the cracked dome to shelter under artificial stars.',
    ],
    [
      'Stillwater Pool',
      'Calm the electric current running through healing water.',
    ],
    ['Silent Chapel', 'Silence the alarm to awaken a restorative field.'],
    ['Cloud Nursery', 'Gather healing mist from the nursery’s vents.'],
    ['Dream Chamber', 'Navigate a dream loop and wake with renewed strength.'],
    ['Dawn Terrace', 'Redirect the lamps to charge a regeneration bench.'],
    ['Moss Cathedral', 'Clear corrupted roots from the living sanctuary.'],
    [
      'Hushed Observatory',
      'Close the shutters before settling into a healing pod.',
    ],
    ['Warm Hearth', 'Reignite the shelter’s generator to restore its warmth.'],
  ]),
  type('trial', 'Trial', '#ff927b', 70, 4, 7, 0, false, [
    [
      'Blade Gauntlet',
      'Cross a corridor of blades to earn the victor’s purse.',
    ],
    ['Gravity Trial', 'Stay upright as the arena turns around you.'],
    ['Thunder Bridge', 'Race lightning across a narrow suspended bridge.'],
    ['Sentinel Ring', 'Outlast the sentinels guarding a bounty of shards.'],
    ['Crushing Hall', 'Beat the timing of the hall’s hydraulic presses.'],
    ['Firewall Sprint', 'Dash between waves of searing security light.'],
    ['Champion Circuit', 'Complete the Core’s abandoned combat simulation.'],
    ['Falling Platforms', 'Leap across platforms as they drop into the dark.'],
    ['Pressure Maze', 'Find a route through a maze of armed pressure plates.'],
    ['Overload Arena', 'Survive the reactor’s surge to collect the prize.'],
  ]),
  type('armory', 'Armory', '#89bbff', 68, 4, 1, 0, true, [
    ['Sentinel Armory', 'Disarm a sentry to claim its relic and spare shards.'],
    [
      'Weapons Bench',
      'Stabilize an unfinished weapon with a relic at its core.',
    ],
    ['Shield Depot', 'Break the depot’s shield and recover a stored artifact.'],
    ['Siege Locker', 'Open a reinforced locker before its traps deploy.'],
    ['War Machine Bay', 'Pull a relic from a dormant war machine.'],
    ['Arsenal Lift', 'Stop a freight lift carrying experimental equipment.'],
    ['Combat Foundry', 'Quench a fresh artifact before the furnace erupts.'],
    ['Officer’s Cache', 'Defeat the cache’s biometric lock to claim its gear.'],
    ['Turret Workshop', 'Avoid tracking barrels while raiding the workbench.'],
    ['Core Supply Line', 'Hijack a shipment of relics meant for the Core.'],
  ]),
  type('shrine', 'Shrine', '#f5a9e1', 64, 3, 0, 2, true, [
    ['Neon Altar', 'Align the altar’s lights to receive a relic and healing.'],
    ['Oracle Node', 'Answer the oracle’s signal to unlock its blessing.'],
    ['Starwell', 'Draw a relic and restorative light from a fallen star.'],
    ['Circuit Monastery', 'Restore a ritual circuit to receive its offering.'],
    ['Prism Oracle', 'Find the true reflection in the oracle’s prism.'],
    ['Pilgrim’s Rest', 'Complete a pilgrim’s unfinished signal offering.'],
    ['Luminous Font', 'Purify the font to reveal the artifact beneath it.'],
    ['Echo Altar', 'Repeat an ancient pulse pattern to earn its blessing.'],
    ['Digital Grove', 'Free the grove’s spirit from a corrupted relay.'],
    [
      'Beacon of Grace',
      'Relight the beacon to claim its restorative artifact.',
    ],
  ]),
  type('supply', 'Supply', '#d0e88b', 42, 2, 1, 2, false, [
    ['Supply Station', 'Unlock a crate of shard cells and medical packs.'],
    ['Ration Store', 'Clear the jammed door to reach a stocked storeroom.'],
    ['Aid Drop', 'Catch a drifting capsule carrying aid and currency.'],
    [
      'Quartermaster’s Desk',
      'Decode the desk’s lock to recover emergency supplies.',
    ],
    ['Transit Depot', 'Redirect a supply cart from the damaged rail.'],
    ['Sealed Pantry', 'Vent the pantry before collecting its useful stock.'],
    ['Field Hospital', 'Recover medicine and spare cells from a ruined tent.'],
    ['Emergency Parcel', 'Defuse the parcel’s security tag to open it.'],
    ['Rescue Shuttle', 'Restore power to the shuttle’s supply compartment.'],
    ['Wayfarer’s Stash', 'Follow a traveler’s marks to a hidden aid cache.'],
  ]),
  type('anomaly', 'Anomaly', '#b9a4ff', 76, 5, 3, 3, true, [
    [
      'Fractured Reality',
      'Reach through a fracture for shards, healing, and a relic.',
    ],
    ['Time Knot', 'Untie a moment holding a traveler’s lost supplies.'],
    ['Mirror Rift', 'Steal a fully stocked cache from your reflection.'],
    ['Inverted Room', 'Walk the ceiling to reach a reality-bending reward.'],
    ['Quantum Orchard', 'Harvest an impossible branch before it vanishes.'],
    ['Static Sea', 'Navigate the static to an island of forgotten rewards.'],
    [
      'Parallel Vault',
      'Open two overlapping vaults at exactly the same instant.',
    ],
    ['Clockless Station', 'Escape a frozen second with its suspended cargo.'],
    [
      'Looping Path',
      'Follow a route that keeps rewriting itself to a hidden cache.',
    ],
    ['Event Horizon', 'Pull a relic and supplies from the edge of oblivion.'],
  ]),
];

export const getDoorType = (kind) =>
  DOOR_TYPES.find((door) => door.kind === kind);

export const createDoor = (encounterId, floor) => {
  const door = DOOR_TYPES.find((candidate) =>
    candidate.encounters.some((encounter) => encounter.id === encounterId)
  );
  if (!door) return null;
  const index = door.encounters.findIndex(
    (encounter) => encounter.id === encounterId
  );
  return {
    ...door.encounters[index],
    kind: door.kind,
    target: Math.min(100, door.target + (floor - 1) * 4 + (index % 3) * 2),
    damage: door.damage + (floor >= 3 ? 1 : 0),
    shards: door.shards,
    heal: door.heal ? door.heal + (index % 2) : 0,
    bonusRelic: door.bonusRelic,
  };
};

export const generateDoorOffers = (random = Math.random) => {
  const offers = {};
  const used = new Set();
  for (let floor = 1; floor <= REGULAR_SECTORS; floor += 1) {
    const types = [...DOOR_TYPES];
    offers[floor] = Array.from({ length: 2 }, () => {
      const [door] = types.splice(Math.floor(random() * types.length), 1);
      const pool = door.encounters.filter(({ id }) => !used.has(id));
      const encounter = pool[Math.floor(random() * pool.length)];
      used.add(encounter.id);
      return encounter.id;
    });
  }
  return offers;
};

export const getDoorReward = (room) =>
  [
    room.shards > 0 && `+${room.shards} shards`,
    room.heal > 0 && `+ ${room.heal} Max HP`,
    room.bonusRelic && '1 random unowned relic',
  ]
    .filter(Boolean)
    .join(', ');
